import React, { useState } from "react";
import { ArrowUpRight, Pill, Users, Activity, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import { StoryCarousel } from "@/components/StoryCarousel";
import useSEO from "@/hooks/useSEO";
import { useLang } from "@/context/LangContext";
import { Link } from "react-router-dom";
import { Languages } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1777269749032-d8d458ae594d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxjYWxtJTIwbW9kZXJuJTIwaG9zcGl0YWwlMjBpbnRlcmlvciUyMGNsaW5pY3xlbnwwfHx8fDE3Nzg3NDM1MDV8MA&ixlib=rb-4.1.0&q=85";

const pathways = [
  { icon: Pill, title: "Medical Detox", desc: "Withdrawal is managed by physicians, 24×7, in a safe, calm setting." },
  { icon: Activity, title: "Behavioural Therapy", desc: "CBT, motivational interviewing and group therapy delivered daily." },
  { icon: Users, title: "Family Integration", desc: "Family is involved from week one — recovery is a system, not a solo act." },
  { icon: ShieldCheck, title: "Relapse Prevention", desc: "Structured aftercare, sober networks, and pharmacological support." },
];

const treats = [
  "Alcohol Use Disorder",
  "Opioid Dependence",
  "Tobacco / Nicotine",
  "Cannabis Dependence",
  "Prescription Drug Misuse",
  "Behavioural Addictions",
  "Gaming & Internet",
  "Polysubstance Use",
];

const steps = [
  { t: "Week 1 — Stabilise", d: "Medical assessment, withdrawal management, sleep & nutrition restoration." },
  { t: "Weeks 2–4 — Understand", d: "Individual therapy uncovers the why behind the use; psychoeducation begins." },
  { t: "Weeks 5–8 — Rebuild", d: "Family sessions, vocational planning, and structured group therapy take centre stage." },
  { t: "Weeks 9–12 — Sustain", d: "Aftercare plan, sober community integration, and ongoing pharmacotherapy review." },
];

export default function Deaddiction() {
  useSEO({
    title: "Deaddiction Centre Bhavnagar — Alcohol, Drugs, Tobacco",
    description: "Medically supervised deaddiction programme in Bhavnagar — inpatient & outpatient. Alcohol, opioids, tobacco, cannabis, behavioural addictions. By Drs. Kamlesh & Dharmin Shah, Manas Neuropsychiatry Hospital.",
    image: HERO,
    lang: "en",
    alternates: [
      { hreflang: "en-IN", href: "https://manasneuropsychiatryhospital.in/deaddiction" },
      { hreflang: "gu-IN", href: "https://manasneuropsychiatryhospital.in/gu/deaddiction" },
      { hreflang: "x-default", href: "https://manasneuropsychiatryhospital.in/deaddiction" },
    ],
  });
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  return (
    <div data-testid="deaddiction-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
        <p className="eyebrow text-[#A39171] mb-6">{t("Deaddiction Programme")}</p>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-5xl">
          {t("Recovery is a road —")} <span className="italic text-[#E07A5F]">{t("we walk it with you")}</span>.
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-2xl leading-relaxed">
          {t("A medically supervised, dignified approach to addiction. Inpatient and outpatient pathways, delivered by addiction medicine specialists and therapists.")}
        </p>
        <Button data-testid="dd-hero-book" onClick={() => setOpen(true)} className="mt-10 rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 font-medium">
          {t("Begin Confidentially")} <ArrowUpRight className="ml-1 w-4 h-4" />
        </Button>
        <div>
          <Link
            to="/gu/deaddiction"
            data-testid="dd-switch-gujarati"
            className="mt-6 inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-[#A39171]/40 text-[#5C524E] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors"
          >
            <Languages className="w-4 h-4" /> ગુજરાતીમાં વાંચો — વ્યસનમુક્તિ
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9]">
          <img src={HERO} alt="Quiet recovery corridor" className="w-full h-full object-cover" />
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="eyebrow text-[#A39171] mb-4">{t("Four Pillars")}</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
              {t("A programme built around")} <span className="italic text-[#E07A5F]">{t("staying well")}</span>.
            </h2>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {pathways.map((p, i) => (
              <div key={p.title} data-testid={`dd-pillar-${i}`}
                className="bg-[#F4EFE6] rounded-3xl p-7 border border-[#A39171]/15 hover:border-[#E07A5F]/40 transition-all">
                <span className="w-12 h-12 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center mb-5">
                  <p.icon className="w-5 h-5" />
                </span>
                <h3 className="font-display text-2xl text-[#2D2623] tracking-tight">{t(p.title)}</h3>
                <p className="text-[#5C524E] mt-2 text-sm leading-relaxed">{t(p.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="eyebrow text-[#A39171] mb-4">{t("Conditions We Treat")}</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
              {t("No addiction is too complex")}<br /> {t("or")} <span className="italic text-[#E07A5F]">{t("too quiet")}</span>.
            </h2>
            <p className="mt-6 text-[#5C524E] text-lg">
              {t("From substance dependence to behavioural addictions, our team treats the spectrum — with the same calm rigour.")}
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-3">
            {treats.map((tr, i) => (
              <div key={tr} data-testid={`dd-treat-${i}`}
                className="bg-[#FDFBF7] rounded-2xl px-5 py-5 border border-[#A39171]/15 flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F]" />
                <span className="text-[#2D2623] font-medium text-sm md:text-base">{t(tr)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">{t("What to expect")}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14">{t("Your first 90 days.")}</h2>
          <ol className="space-y-10">
            {steps.map((s, i) => (
              <li key={s.t} data-testid={`dd-step-${i}`} className="grid grid-cols-12 gap-6 border-b border-[#A39171]/15 pb-10 last:border-none">
                <div className="col-span-12 md:col-span-3">
                  <span className="font-display text-4xl text-[#E07A5F]">0{i + 1}</span>
                </div>
                <div className="col-span-12 md:col-span-9">
                  <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight">{t(s.t)}</h3>
                  <p className="text-[#5C524E] mt-3 text-lg">{t(s.d)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-[#F4EFE6]/40 py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <StoryCarousel
            category="deaddiction"
            eyebrow={t("Recovery, in their words")}
            heading={
              <>
                {t("People who thought")} <span className="italic text-[#E07A5F]">{t("they were beyond help")}</span>.
              </>
            }
            note={t("Anonymous accounts from patients and families treated in our deaddiction programme. Names and identifying details have been removed.")}
          />
        </div>
      </section>

      <section className="bg-[#2D2623] text-[#FDFBF7] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center relative">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
            {t("Recovery starts with one")} <span className="italic text-[#E07A5F]">{t("honest phone call")}</span>.
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="dd-cta-book" onClick={() => setOpen(true)} className="rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white px-8 h-12 font-medium">{t("Request Appointment")}</Button>
            <a href="tel:+919427560101" className="rounded-full border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors">{t("Call")} +91 94275 60101</a>
          </div>
        </div>
      </section>

      <AppointmentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
